import { createWebHistory, createRouter } from "vue-router";
import { useAuthStore } from "./store/AuthStore";
import { useCurrentUserStore } from "./pinia/CurrentUserStore";
import store from "./store";
import axios from "axios";
import * as Sentry from "@sentry/vue";
import { claimRoutes } from "./router_claims";
import { popupRoutes } from "./PopupRoutes";
import { can } from '@/components/permissions-handler/helpers/usePermissions'
import PurchaseTable from "./components/kooi/PurchaseTable.vue";
import { useRouteHelperStore } from "./pinia/RouteHelperStore";

const Login = () => import("@/components/Login.vue");
const Dashboard = () => import("@/components/dashboard/dashboard-main.vue");
const Sidebar = () => import("@/components/layouts/Sidebar.vue");
const Products = () => import("@/components/products/products-page.vue");
const pobox = () => import("@/components/email/po-box.vue");
const ProductCategories = () =>
    import("@/components/products/product-category-page.vue");
const Pricelists = () => import("@/components/pricelists/pricelists-page.vue");
const Units = () => import("@/components/units/units-page.vue");
const Currencies = () => import("@/components/currencies/currencies-page.vue");
const Companies = () => import("@/components/companies/CompaniesTable.vue");
const CompaniesEdit = () =>
    import("@/components/companies/CompanyEditPage.vue");

const DimensionTypesTable = () => import("@/components/products/Dimensions/DimensionTypesTable.vue");
const DimensionTypesEdit = () => import("@/components/products/Dimensions/DimensionTypesEdit.vue");

const ExternalInvoicesTable = () => import("@/components/ExternalInvoices/ExternalInvoicesTable.vue");
const ExternalInvoicesEdit = () => import("@/components/ExternalInvoices/ExternalInvoicesEdit.vue");

const Logistics = () => import("@/components/logistics/logistic-settings.vue");
const Conditions = () =>
    import("@/components/conditions/condition-settings.vue");
const Textblocks = () =>
    import("@/components/textblocks/textblocks-overview.vue");
const Countries = () => import("@/components/countries/countries-page.vue");
const Emailtemplates = () =>
    import("@/components/emailtemplates/emailtemplates-page.vue");
const PdfTranslations = () => import("@/components/system/PdfTranslations.vue");
const SystemSettings = () =>
    import("@/components/system/system-settings-page.vue");
const Users = () => import("@/components/user/user-page.vue");

const GroupIndex = () => import('./../../vendor/netattalk-composer/permissions-handler/src/vuejs/3/views/PermissionGroupIndex.vue');
const GroupEdit = () => import('./../../vendor/netattalk-composer/permissions-handler/src/vuejs/3/views/PermissionGroupEdit.vue');
const RoleIndex = () => import('./../../vendor/netattalk-composer/permissions-handler/src/vuejs/3/views/RoleIndex.vue')
const RoleEdit = () => import('./../../vendor/netattalk-composer/permissions-handler/src/vuejs/3/views/RoleEdit.vue')

const QuickOffer = () => import("@/components/kooi/quick-offer.vue");

const KooiMain = () => import("@/components/kooi/KooiMain.vue");
const NoteMain = () => import("./components/note/note-main.vue");

const KooiBaseData = () => import("./components/kooi/kooi-base-data.vue");
const InvoiceTable = () => import("./components/kooi/InvoiceTable.vue");
const OrderMain = () => import("./components/kooi/order-main.vue");
const EmailTable = () => import("./components/kooi/EmailTable.vue");
const LogisticsMain = () => import("./components/logistics/logistics-main.vue");
const SuppliersMain = () => import("./components/supplier/suppliers-main.vue");
const ConditionsMain = () =>
    import("./components/conditions/conditions-main.vue");
const InvoiceCreate = () => import("./components/invoice/InvoiceCreate.vue");
const BalanceMain = () => import("./components/kooi/balance-main.vue");
const ReleaseTable = () => import("./components/releases/ReleaseTable.vue");
const ReleaseMain = () => import("./components/releases/release-main.vue");

const InterestTable = () => import('./components/kooi/InterestTable.vue');
const ClaimTable = () => import('./components/kooi/ClaimTable.vue');

const shinkibaMain = () => import("./components/shinkiba/shinkiba-main.vue");
const MycroftMain = () => import("./components/mycroft/mycroft-main.vue");
const OpList = () => import("./components/reporting/OpList.vue");
const ReportDynamicList = () =>
    import("./components/reporting/DefaultDynamicList.vue");
const InvoiceOverviewPage = () => import('./components/invoice/InvoiceOverviewPage.vue');
const ProfitList = () => import("./components/reporting/ProfitDynamicList.vue");
const ProvisionList = () =>
    import("./components/reporting/ProvisionDynamicList.vue");
const RebateList = () =>
    import("./components/reporting/RebateDynamicList.vue");

const TableColumnIndex = () =>
    import("./components/tableColumns/TableColumnIndex.vue");
const TableColumnEdit = () =>
    import("./components/tableColumns/TableColumnEdit.vue");

const SettingsPage = () => import("./components/settings/SettingsPage.vue");
const PossibleIssuesList = () => import("./components/reporting/PossibleIssuesList.vue");

const AdjustmentCalculations = () => import("./components/AdjustmentCalculations/AdjustmentCalculationsTable.vue");
const AdjustmentCalculationsEdit = () => import("./components/AdjustmentCalculations/AdjustmentCalculationsEdit.vue");
const AdjustmentCalculationList = () => import("./components/reporting/AdjustmentCalculationList.vue");


const FinancialPanel = () => import("./components/claims/Relations/ClaimFinancialPanel.vue");

const routes = [
    {
        name: "login",
        path: "/login",
        component: Login,
        meta: {
            middleware: "guest",
            title: `Login`,
            requiresAuth: false,
        },
    },
    {
        path: "/",
        component: Sidebar,
        meta: {
            middleware: "auth",
            requiresAuth: true,
        },
        children: [
            {
                name: "pobox_inbox",
                path: "/pobox/:filter",
                component: pobox,
                meta: {
                    middleware: "auth",
                    title: `Inbox`,
                    requiresAuth: true,
                    permission: "inbox.show",
                },
            },
            {
                name: "pobox_sent",
                path: "/pobox/:filter",
                component: pobox,
                meta: {
                    middleware: "auth",
                    title: `Sent`,
                    requiresAuth: true,
                    permission: "inbox.show",
                },
            },
            {
                name: "pobox_outgoing",
                path: "/pobox/:filter",
                component: pobox,
                meta: {
                    middleware: "auth",
                    title: `Outgoing`,
                    requiresAuth: true,
                    permission: "inbox.show",
                },
            },
            {
                name: "quick_offer",
                path: "/quick_offer",
                component: QuickOffer,
                meta: {
                    middleware: "auth",
                    title: `Quick Offer`,
                    requiresAuth: true,
                },
            },
            {
                name: "dashboard",
                path: "/dashboard",
                component: Dashboard,
                meta: {
                    middleware: "auth",
                    title: `Dashboard`,
                    requiresAuth: true,
                    permission: "dashboard.show",
                },
            },
            {
                name: "products",
                path: "/products",
                component: Products,
                meta: {
                    middleware: "auth",
                    title: `Products`,
                    requiresAuth: true,
                    permission: "products.show",
                },
            },
            {
                name: "productcategories",
                path: "/productcategories",
                component: ProductCategories,
                meta: {
                    middleware: "auth",
                    title: `Productcategories`,
                    requiresAuth: true,
                    permission: "categories.show",
                },
            },
            {
                name: "pricelists",
                path: "/pricelists",
                component: Pricelists,
                meta: {
                    middleware: "auth",
                    title: `Pricelists`,
                    requiresAuth: true,
                    permission: "price_list.show",
                },
            },
            {
                name: "units",
                path: "/units",
                component: Units,
                meta: {
                    middleware: "auth",
                    title: `Units`,
                    requiresAuth: true,
                    permission: "units.show",
                },
            },
            {
                name: "currencies",
                path: "/currencies",
                component: Currencies,
                meta: {
                    middleware: "auth",
                    title: `Currencies`,
                    requiresAuth: true,
                    permission: "currencies.show",
                },
            },
            {
                name: "dimensionTypes",
                path: "/dimension-types",
                component: DimensionTypesTable,
                meta: {
                    middleware: "auth",
                    title: `Dimension Types`,
                    requiresAuth: true,
                    permission: "dimension-types.show",
                },
            },
            {
                name: "dimensionTypes.edit",
                path: "/dimension-types/:id",
                component: DimensionTypesEdit,
                meta: {
                    middleware: "auth",
                    title: `Dimension Types Edit`,
                    requiresAuth: true,
                    permission: "dimension-types.edit",
                },
            },
            {
                name: "companies",
                path: "/companies",
                component: Companies,
                meta: {
                    middleware: "auth",
                    title: `Companies`,
                    requiresAuth: true,
                    permission: "companies.show",
                    skip_redirect: true,
                },
            },
            {
                name: "companies.edit",
                path: "/company/:id?",
                component: CompaniesEdit,
                meta: {
                    middleware: "auth",
                    title: `Company Edit`,
                    requiresAuth: true,
                    permission: "companies.show",
                    skip_redirect: true,
                },
            },
            {
                name: "logistics",
                path: "/logistics",
                component: Logistics,
                meta: {
                    middleware: "auth",
                    title: `Logistics`,
                    requiresAuth: true,
                    permission: "logistics.show",
                },
            },
            {
                name: "conditions",
                path: "/conditions",
                component: Conditions,
                meta: {
                    middleware: "auth",
                    title: `Conditions`,
                    requiresAuth: true,
                    permission: "conditions.show",
                },
            },
            {
                name: "textblocks",
                path: "/textblocks",
                component: Textblocks,
                meta: {
                    middleware: "auth",
                    title: `Textblocks`,
                    requiresAuth: true,
                    permission: "textblocks.show",
                },
            },
            {
                name: "countries",
                path: "/countries",
                component: Countries,
                meta: {
                    middleware: "auth",
                    title: `Countries`,
                    requiresAuth: true,
                    permission: "countries.show",
                },
            },
            {
                name: "email_templates",
                path: "/email_templates",
                component: Emailtemplates,
                meta: {
                    middleware: "auth",
                    title: `Email Templates`,
                    requiresAuth: true,
                    permission: "e-mail-templates.show",
                },
            },
            {
                name: "externalInvoices",
                path: "/ext-invoices",
                component: ExternalInvoicesTable,
                meta: {
                    middleware: "auth",
                    title: `External Invoice`,
                    requiresAuth: true,
                    permission: "external-invoices.show",
                },
            },
            {
                name: "externalInvoices.edit",
                path: "/ext-invoices/:id",
                component: ExternalInvoicesEdit,
                meta: {
                    middleware: "auth",
                    title: `External Invoice Edit`,
                    requiresAuth: true,
                    permission: "external-invoices.edit",
                },
            },

            {
                name: "pdf_translations",
                path: "/pdf_translations",
                component: PdfTranslations,
                meta: {
                    middleware: "auth",
                    title: `Pdf Translations`,
                    requiresAuth: true,
                    permission: "pdf-translation.show",
                },
            },
            {
                name: "system_settings",
                path: "/system_settings",
                component: SystemSettings,
                meta: {
                    middleware: "auth",
                    title: `System Settings`,
                    requiresAuth: true,
                    permission: "system_settings.show",
                },
            },
            {
                name: "users",
                path: "/users",
                component: Users,
                meta: {
                    middleware: "auth",
                    title: `Users`,
                    requiresAuth: true,
                    permission: "users.show",
                },
            },
            {
                name: "permission_groups.index",
                path: "/groups",
                component: GroupIndex,
                meta: {
                    middleware: "auth",
                    title: `Groups`,
                    requiresAuth: true,
                    permission: "groups.show",
                },
            },
            {
                name: "permission_groups.edit",
                path: "/groups/:id",
                component: GroupEdit,
                meta: {
                    middleware: "auth",
                    title: `Groups`,
                    requiresAuth: true,
                    permission: "groups.edit"
                }
            },
            {
                name: "permission_roles.index",
                path: "/roles",
                component: RoleIndex,
                meta: {
                    middleware: "auth",
                    title: `Roles`,
                    requiresAuth: true,
                    permission: "groups.show",
                }
            },
            {
                name: "permission_roles.edit",
                path: "/roles/:id",
                component: RoleEdit,
                meta: {
                    middleware: "auth",
                    title: `Roles`,
                    requiresAuth: true,
                    permission: "groups.edit",
                }
            },
        ],
    },
    {
        path: "/table",
        component: Sidebar,
        meta: {
            middleware: "auth",
            title: "Table Columns",
            requiresAuth: true,
        },
        children: [
            {
                name: "table-columns.index",
                path: "/columns",
                component: TableColumnIndex,
                meta: {
                    middleware: "auth",
                    title: `Companies`,
                    requiresAuth: true,
                    permission: "table_columns.edit",
                },
            },
            {
                name: "table-columns.edit",
                path: "/columns/:id",
                component: TableColumnEdit,
                meta: {
                    middleware: "auth",
                    title: `Company Edit`,
                    requiresAuth: true,
                    permission: "table_columns.edit",
                },
            },
        ],
    },
    {
        path: "/reporting",
        component: Sidebar,
        meta: {
            middleware: "auth",
            title: `KOOI`,
            requiresAuth: true,
            skip_redirect: true,
        },
        children: [
            {
                name: "reporting.invoices-overview",
                path: "/reporting/invoices_overview",
                component: InvoiceOverviewPage,
                meta: {
                    middleware: "auth",
                    title: `Invoices`,
                    requiresAuth: true,
                    permission: "oplist.show",
                },
            },
            {
                name: "reporting.invoices",
                path: "/reporting/invoices",
                component: OpList,
                meta: {
                    middleware: "auth",
                    title: `OP-List`,
                    requiresAuth: true,
                    permission: "oplist.show",
                },
            },
            {
                name: "reporting.profit",
                path: "/reporting/:type/list",
                component: ProfitList,
                meta: {
                    middleware: "auth",
                    title: `Profit List`,
                    requiresAuth: true,
                    permission: "oplist.show",
                },
            },
            {
                name: "reporting.provisions",
                path: "/reporting/:type/show",
                component: ProvisionList,
                meta: {
                    middleware: "auth",
                    title: `Provision List`,
                    requiresAuth: true,
                    permission: "oplist.show",
                },
            },
                        {
                name: "reporting.adjustment_calculations",
                path: "/report/:type",
                component: AdjustmentCalculationList,
                meta: {
                    middleware: "auth",
                    title: `adjustment_calculations List`,
                    requiresAuth: true,
                    permission: "adjustment_calculations.show",
                },
            },
            {
                name: "reporting.type",
                path: "/reporting/:type",
                component: ReportDynamicList,
                meta: {
                    middleware: "auth",
                    title: `Reporting List`,
                    requiresAuth: true,
                    permission: "oplist.show",
                },
            },
            {
                name: "reporting.rebate",
                path: "/reporting/rebate/:type/show",
                component: RebateList,
                meta: {
                    middleware: "auth",
                    title: `Rebate List`,
                    requiresAuth: true,
                    permission: "oplist.show",
                },
            },
                        {
                name: "reporting.possible_issues",
                path: "/reporting/issues/:type/show",
                component: PossibleIssuesList,
                meta: {
                    middleware: "auth",
                    title: `Rebate List`,
                    requiresAuth: true,
                    permission: "oplist.show",
                },
            },

        ],
    },
    {
        path: "/kooi",
        component: Sidebar,
        meta: {
            middleware: "auth",
            title: `KOOI`,
            requiresAuth: true,
            skip_redirect: true,
        },
        children: [
            {
                path: ":id",
                component: KooiMain,
                children: [
                    {
                        name: "kooi.kooi-base-data",
                        path: ":type?",
                        component: KooiBaseData,
                    },
                    {
                        path: "typ/:type?",
                        children: [
                            {
                                name: "kooi.invoice-table",
                                path: "",
                                component: InvoiceTable,
                            },
                            {
                                name: "kooi.purchase-table",
                                path: "purchase",
                                component: PurchaseTable,
                            },
                            {
                                name: "kooi.invoice-edit",
                                path: "edit/:document_id",
                                component: InvoiceCreate,
                            },
                            {
                                name: "kooi.order-main",
                                path: "order",
                                component: OrderMain,
                            },
                            {
                                name: "kooi.order-edit",
                                path: "order/:document_id",
                                component: OrderMain,
                            },
                            {
                                name: "kooi.release-table",
                                path: "releases",
                                component: ReleaseTable,
                            },
                            {
                                name: "kooi.release-edit",
                                path: "releases/:document_id",
                                component: ReleaseMain,
                            },
                            {
                                name: "kooi.interest-table",
                                path: "interest",
                                component: InterestTable,
                            },
                            {
                                name: "kooi.claim-table",
                                path: "claims",
                                component: ClaimTable,
                            },
                            {
                                name: "kooi.adjustment_calculations-table",
                                path: "adjustments",
                                component: AdjustmentCalculations,
                            },
                            {
                                name: "kooi.adjustment_calculations-edit",
                                path: "adjustments/:document_id",
                                component: AdjustmentCalculationsEdit,
                                permission : "adjustment_calculations.edit",
                            },
                            {
                                name: "kooi.finances-audit",
                                path: "finances",
                                component: FinancialPanel,
                            },
                        ],
                    },
                    {
                        name: "kooi.email-table",
                        path: "communication",
                        component: EmailTable,
                    },
                    {
                        name: "kooi.shinkiba-main",
                        path: "shinkiba",
                        component: shinkibaMain,
                    },
                    {
                        name: "kooi.mycroft-main",
                        path: "mycroft",
                        component: MycroftMain,
                    },
                    {
                        name: "kooi.logistics-main",
                        path: "logistic",
                        component: LogisticsMain,
                    },
                    {
                        name: "kooi.suppliers-main",
                        path: "supplier",
                        component: SuppliersMain,
                    },
                    {
                        name: "kooi.conditions-main",
                        path: "conditions",
                        component: ConditionsMain,
                    },

                    {
                        name: "kooi.balance-main",
                        path: "balance",
                        component: BalanceMain,
                        meta: {
                            permission: "kooi.balance_show",
                        },
                    },
                    {
                        name: "kooi.note-main",
                        path: "notes",
                        component: NoteMain,
                    },
                ],
            },
        ],
    },
    {
        path: "/",
        component: Sidebar,
        meta: {
            middleware: "auth",
            requiresAuth: true,
        },
        children: [
            {
                name: "settings.registry",
                path: "/admin/registry",
                component: SettingsPage,
                meta: {
                    middleware: "auth",
                    title: `Registry`,
                    requiresAuth: true,
                    permission: "registry.show",
                },
            },
        ],
    },
    claimRoutes,
    popupRoutes,
];

const router = createRouter({
    history: createWebHistory(pagesettings.vueBaseUrl),
    routes, // short for `routes: routes`
});

if (
    pagesettings.user !== null &&
    pagesettings.user !== undefined &&
    pagesettings.user.api_token !== null &&
    pagesettings.user.api_token !== undefined &&
    pagesettings.user.api_token != ""
) {
    useAuthStore.authenticated = true;
}

router.beforeEach(() => {
    const routeHelperStore = useRouteHelperStore();
    if(routeHelperStore.disable_navigation == true){
        return false;
    }
})

router.beforeEach((to, from, next) => {
    document.title = to.meta.title;

    const requiresAuth = to.meta.requiresAuth;
    const currentUser = useCurrentUserStore();
    if (requiresAuth && !useAuthStore.authenticated) {
        //requiresAuth && !useAuthStore.authenticated
        next("login");
    } else {
        if (
            useAuthStore.user &&
            useAuthStore.user.id &&
            useAuthStore.user.email
        ) {
            Sentry.setUser({
                id: useAuthStore.user.id,
                email: useAuthStore.user.email,
            });
        }
        if (!useAuthStore.authenticated) {
            axios.get("/sanctum/csrf-token").then(() => {
                return axios
                    .get("/api/user")
                    .then(({ data }) => { })
                    .catch((error) => {
                        currentUser.user = {};
                        console.log(error);
                    });
            });
        }
        if (useAuthStore.authenticated && to.name == "login") {
            next("dashboard");
        } else {
            //store.commit("browserTabs/addTab", {route : {name : to.name, params: to.params, title: " ", duplicateIdentifier : to.name + to.params.id ??"" }})
            next();
        }
    }
});

router.beforeEach((to, from, next) => {
    const currentUser = useCurrentUserStore();
    if (
        currentUser.user &&
        currentUser.user.is_administrator &&
        currentUser.user.is_administrator === "true"
    ) {
        next();
        return;
    }
    if (!to.meta || !to.meta.permission) {
        next();
        return;
    }
    if (
        to.meta.permission &&
        can(to.meta.permission)
    ) {
        next();
        return;
    }
    next(from);
});

export default router;
